import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import {required} from '@vuelidate/validators';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import Resource from './Resource';
import Activity from './Activity';
import {RESOURCE_CATEGORIES} from '@/../../app/src/helpers/constants';

export default class Staff extends BaseModel {
  constructor({
    id,
    number,
    userId,
    name = undefined,
    firstName = '',
    lastName = '',
    active,
    gender = undefined,
    houseNo = undefined,
    boxNo = undefined,
    postcode = undefined,
    city = undefined,
    country = undefined,
    memo = undefined,
    resource = undefined,
    defaultActivity = undefined,
    defaultWorkingHours1 = undefined,
    defaultWorkingHours2 = undefined,
    defaultWorkingHours3 = undefined,
    defaultWorkingHours4 = undefined,
    defaultWorkingHours5 = undefined,
    defaultWorkingHours6 = undefined,
    defaultWorkingHours7 = undefined,
    customData = undefined,
    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
    designation = undefined,
    first_name = undefined,
    last_name = undefined,
    nssoNo = undefined,
    nssoLimosaNo = undefined,
  }) {
    super(id, number || no, name || designation);
    this.name = name || `${firstName || ''} ${lastName || ''}`;
    this.userId = userId;
    this.firstName = firstName || first_name;
    this.lastName = lastName || last_name;
    this.active = active;
    this.gender = gender
      ? HitOnlineEnums.getEnumValue('GENDER', gender, translate)
      : null;
    this.houseNo = houseNo;
    this.boxNo = boxNo;
    this.postcode = postcode;
    this.city = city;
    this.country = country;
    this.memo = memo;
    this.resource = resource ? new Resource(resource) : null;
    this.defaultActivity = defaultActivity
      ? new Activity(defaultActivity)
      : null;
    this.defaultWorkingHours1 = defaultWorkingHours1;
    this.defaultWorkingHours2 = defaultWorkingHours2;
    this.defaultWorkingHours3 = defaultWorkingHours3;
    this.defaultWorkingHours4 = defaultWorkingHours4;
    this.defaultWorkingHours5 = defaultWorkingHours5;
    this.defaultWorkingHours6 = defaultWorkingHours6;
    this.defaultWorkingHours7 = defaultWorkingHours7;
    this.customData = customData;
    this.nssoNo = nssoNo;
    this.nssoLimosaNo = nssoLimosaNo;
  }

  get fullDesignation() {
    return HitUtils.createStandardDesignation({
      number: this.number,
      designation:
        this.firstName || this.lastName
          ? `${this.firstName || ''} ${this.lastName || ''}`
          : this.designation,
    });
  }

  /**
   * Only join the necessary attributes for the designation
   */
  static get joinAttrsForDesignation() {
    const designationKeys = ['number', 'firstName', 'lastName'];
    return designationKeys
      .map((key) => this.allAttributes.find((attr) => attr.name === key))
      .filter((attr) => attr !== undefined);
  }

  static get baseModuleForRedirection() {
    return 'staff-base';
  }
  get baseModuleForRedirection() {
    return Staff.baseModuleForRedirection;
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'asc',
    };
  }
  get defaultSort() {
    return Staff.defaultSort;
  }

  static get fkName() {
    return 'staff_id';
  }
  static get apiRoute() {
    return 'staff';
  }
  static get detailPageRoute() {
    return 'staffDetail';
  }
  static get listPageRoute() {
    return 'staffList';
  }
  get fkName() {
    return Staff.fkName;
  }
  get apiRoute() {
    return Staff.apiRoute;
  }
  get listPageRoute() {
    return Staff.listPageRoute;
  }
  get detailPageRoute() {
    return Staff.detailPageRoute;
  }

  static get printTemplateType() {
    return 'staff';
  }
  get printTemplateType() {
    return Staff.printTemplateType;
  }

  static get attrsToStoreInForm() {
    return ['number', 'name', 'firstName', 'lastName', 'resource'];
  }
  get attrsToStoreInForm() {
    return Staff.attrsToStoreInForm;
  }

  static get joinAttrsKey() {
    return [
      'userId',
      'number',
      'name',
      'firstName',
      'lastName',
      'locale',
      'resource',
    ];
  }

  static get createInitColumns() {
    // We need to define the column names of the db and not the attribute keys
    return {
      first_name: '',
      last_name: '',
      birthdate: new Date(),
      civilstatus_date: new Date(),
      idcard_date: new Date(),
      entry_date: new Date(),
      exit_date: new Date(),
      drivinglicence_date: new Date(),
    };
  }

  static get listDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        'name',
        'gender',
        'street',
        'houseNo',
        'boxNo',
        'postcode',
        'city',
        'country',
        'active',
        'tags',
        'creationDate',
        'modificationDate',
      ],
      ['userId'],
    );
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        'name',
        'street',
        'houseNo',
        'boxNo',
        'postcode',
        'city',
        'country',
        'active',
        'tags',
      ],
      ['firstName', 'lastName', 'userId', 'resource', 'defaultActivity'],
    );
  }

  // Example of of Model specific dataListConfig
  // static get dataListConfig() {
  //   return {
  //     zone2Field: 'name',
  //     zone3Field: 'city',
  //   };
  // }

  static get confidentialBrowserDisplayAttrs() {
    return this.getAttributes(
      ['number', 'name', 'active', 'tags'],
      [
        'firstName',
        'lastName',
        'userId',
        'resource',
        'defaultActivity',
        'street',
        'houseNo',
        'boxNo',
        'postcode',
        'city',
        'country',
      ],
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'postcode',
        'city',
        'country',
        'planning',
        'timeslotBoardExcluded',
        'active',
        'tags',
        'roles',
      ]),
      'hit-app.common.configuration': this.getAttributes([
        'resource',
        'defaultActivity',
        'defaultWorkingHours1',
        'defaultWorkingHours2',
        'defaultWorkingHours3',
        'defaultWorkingHours4',
        'defaultWorkingHours5',
        'defaultWorkingHours6',
      ]),
      'hit-base.bulk-update.advanced-information': this.getAttributes([
        'citizenship',
        'civilStatus',
        'drivingLicenceCategory',
        'sizeJacket',
        'sizeTrousers',
        'sizeShoes',
        'professionalProfile',
        'professionalProfileComment',
      ]),
      'hit-base.common.competence': [
        {
          name: 'competences',
          tableProperties: {
            header: 'hit-base.common.competence',
          },
        },
      ],
    };
  }
  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '1fr'}),
      new AT.StringAttribute({
        name: 'userId',
        column: 'user_id',
        tableProperties: {
          header: 'hit-app.common.user-id',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'name',
        column: 'designation',
        tableProperties: {
          header: 'hit-base.common.name',
          sharable: true,
          width: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'firstName',
        column: 'first_name',
        validations: {required},
        tableProperties: {
          header: 'hit-app.common.first-name',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'lastName',
        column: 'last_name',
        validations: {required},
        tableProperties: {
          header: 'hit-app.common.last-name',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'street',
        tableProperties: {
          header: 'hit-app.common.street',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'houseNo',
        column: 'house_no',
        tableProperties: {
          header: 'hit-app.common.house-number',
          sharable: true,
          width: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'boxNo',
        column: 'box_no',
        tableProperties: {
          header: 'hit-app.common.box-number',
          sharable: true,
          width: '1fr',
          hideByDefault: true,
        },
      }),
      new AT.StringAttribute({
        name: 'postcode',
        tableProperties: {
          header: 'hit-app.common.postcode',
          maxWidth: '0.5fr',
        },
      }),
      new AT.StringAttribute({
        name: 'city',
        tableProperties: {
          header: 'hit-app.common.city',
          maxWidth: '1fr',
        },
      }),
      new AT.FkCountryAttribute({
        name: 'country',
        joinPath: ['country!fk_staff_country_country'],
        tableProperties: {
          header: 'hit-base.common.country',
          maxWidth: '1fr',
        },
      }),
      new AT.LanguageAttribute({
        name: 'locale',
        joinPath: ['language!fk_staff_locale_locale'],
        tableProperties: {
          header: 'hit-base.common.language',
        },
      }),
      this.ACTIVE_ATTRIBUTE(),
      this.TAGS_ATTRIBUTE({moduleUsage: 'staff', tableOrigin: 'staff'}),
      new AT.BooleanAttribute({
        name: 'planning',
        tableProperties: {
          header: 'hit-app.timeslot.planning-flag',
        },
      }),
      new AT.BooleanAttribute({
        name: 'timeslotBoardExcluded',
        column: 'timeslot_board_excluded',
        tableProperties: {
          header: 'hit-app.staff.exclude-board',
        },
      }),
      new AT.FkResourceAttribute({
        name: 'resource',
        joinPath: ['resource!fk_staff_cost_resource_id_resource'],
        fkToUpdate: 'cost_resource_id',
        formItemName: 'resource',
        tableProperties: {
          header: 'hit-app.common.cost-resource',
        },
        filters: {resource_category_id: `eq.${RESOURCE_CATEGORIES.LABOUR}`},
      }),
      new AT.ArrayAttribute({
        name: 'roles',
        column: 'qualification!staff_qualification(id)',
        transform: (objArray) => objArray.map((x) => x.id),
        tableProperties: {
          header: 'hit-base.common.role',
        },
      }),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      new AT.EnumerationAttribute({
        name: 'gender',
        enumType: 'GENDER',
        enumClass: HitOnlineEnums,
        tableProperties: {
          header: 'hit-app.common.gender',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'birthdate',
        tableProperties: {
          header: 'hit-app.common.birthdate',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'birthplace',
        tableProperties: {
          header: 'hit-app.common.birthplace',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'registrationNo',
        column: 'registration_no',
        tableProperties: {
          header: 'hit-app.common.nationalregisterid',
          sharable: true,
        },
      }),
      new AT.FkCountryAttribute({
        name: 'citizenship',
        joinPath: ['country!fk_staff_country_id_country'],
        column: 'country_id',
        fkToUpdate: 'country_id',
        tableProperties: {
          header: 'hit-app.common.citizenship',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'civilStatus',
        column: 'civilstatus',
        tableProperties: {
          header: 'hit-app.common.civilstatus',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'civilStatusDate',
        column: 'civilstatus_date',
        tableProperties: {
          header: 'hit-app.common.civilstatusdate',
          sharable: true,
        },
      }),
      new AT.IntegerAttribute({
        name: 'personOnCharge',
        column: 'dependants',
        tableProperties: {
          header: 'hit-app.common.persononcharge',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'idCardNo',
        column: 'idcard_no',
        tableProperties: {
          header: 'hit-app.common.idcardno',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'idCardDate',
        column: 'idcard_date',
        tableProperties: {
          header: 'hit-app.common.expirationdate',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'drivingLicenceNo',
        column: 'drivinglicence_no',
        tableProperties: {
          header: 'hit-app.common.drivinglicenceno',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'drivingLicenceDate',
        column: 'drivinglicence_date',
        tableProperties: {
          header: 'hit-app.common.expirationdate',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'drivingLicenceCategory',
        column: 'drivinglicence_category',
        tableProperties: {
          header: 'hit-app.common.drivinglicencecategory',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'emergencyContact',
        column: 'emergency_contact',
        tableProperties: {
          header: 'hit-app.common.emergencycontact',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'civilInfoComment',
        column: 'civilstatus_comment',
        tableProperties: {
          header: 'hit-app.common.civilinfocomment',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'bankName',
        column: 'bank_name',
        tableProperties: {
          header: 'hit-app.common.bankname',
        },
      }),
      new AT.StringAttribute({
        name: 'iban',
        column: 'iban',
        tableProperties: {
          header: 'hit-app.common.iban',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'bic',
        column: 'bic',
        tableProperties: {
          header: 'hit-app.common.bic',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'bankAccountNo',
        column: 'bank_account_no',
        tableProperties: {
          header: 'hit-app.common.bankaccountno',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'professionalProfile',
        column: 'profession',
        tableProperties: {
          header: 'hit-app.common.professionalprofile',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'engagementDate',
        column: 'entry_date',
        tableProperties: {
          header: 'hit-app.common.engagementdate',
          sharable: true,
        },
      }),
      new AT.DateAttribute({
        name: 'redundancyDate',
        column: 'exit_date',
        tableProperties: {
          header: 'hit-app.common.redundancydate',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'redundancyReason',
        column: 'exit_reason',
        tableProperties: {
          header: 'hit-app.common.redundancyreason',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'professionalProfileComment',
        column: 'exit_comment',
        tableProperties: {
          header: 'hit-app.common.professionalprofilecomment',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'sizeJacket',
        column: 'jacket_size',
        tableProperties: {
          header: 'hit-app.common.sizejacket',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'sizeTrousers',
        column: 'trousers_size',
        tableProperties: {
          header: 'hit-app.common.sizetrousers',
          sharable: true,
        },
      }),
      new AT.StringAttribute({
        name: 'sizeShoes',
        column: 'shoe_size',
        tableProperties: {
          header: 'hit-app.common.size-shoes',
          sharable: true,
        },
      }),
      new AT.FkActivityAttribute({
        name: 'defaultActivity',
        fkToUpdate: 'default_activity_id',
        column: 'default_activity_id',
        joinPath: ['activity!fk_staff_default_activity_id_activity'],
        tableProperties: {
          header: 'hit-app.staff.default-activity',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours1',
        column: 'default_working_hours_1',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.monday',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours2',
        column: 'default_working_hours_2',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.tuesday',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours3',
        column: 'default_working_hours_3',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.wednesday',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours4',
        column: 'default_working_hours_4',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.thursday',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours5',
        column: 'default_working_hours_5',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.friday',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours6',
        column: 'default_working_hours_6',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.saturday',
        },
      }),
      new AT.DecimalAttribute({
        name: 'defaultWorkingHours7',
        column: 'default_working_hours_7',
        decimalPrecision: 2,
        tableProperties: {
          header: 'hit-base.common.sunday',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'customData',
        column: 'custom_data',
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.StringAttribute({
        name: 'nssoNo',
        column: 'nsso_no',
        tableProperties: {
          header: 'hit-app.common.nsso-no',
        },
      }),
      new AT.StringAttribute({
        name: 'nssoLimosaNo',
        column: 'nsso_limosa_no',
        tableProperties: {
          header: 'hit-app.common.nsso-limosa-no',
        },
      }),
    ];
  }
}
